import { Controller } from "@hotwired/stimulus"

/**
 * WhnLinkTypeController
 *
 * Handles toggling between Product Line, Category, and Static Page link types
 * in the Wire Harness News ad form. When switching types, this controller
 * shows/hides the appropriate form fields and clears values from the hidden types.
 *
 * @example
 *   <div data-controller="whn-link-type">
 *     <input type="radio" data-whn-link-type-target="productLineRadio" data-action="change->whn-link-type#toggleLinkType">
 *     <input type="radio" data-whn-link-type-target="categoryRadio" data-action="change->whn-link-type#toggleLinkType">
 *     <input type="radio" data-whn-link-type-target="staticPageRadio" data-action="change->whn-link-type#toggleLinkType">
 *     <div data-whn-link-type-target="productLineFields">...</div>
 *     <div data-whn-link-type-target="categoryFields">...</div>
 *     <div data-whn-link-type-target="staticPageFields">...</div>
 *     <select data-whn-link-type-target="productLineSelect">...</select>
 *     <select data-whn-link-type-target="categorySelect">...</select>
 *     <select data-whn-link-type-target="staticPageSelect">...</select>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    "productLineRadio",
    "categoryRadio",
    "staticPageRadio",
    "productLineFields",
    "categoryFields",
    "staticPageFields",
    "productLineSelect",
    "categorySelect",
    "staticPageSelect"
  ]

  /**
   * Toggles the visibility of link type fields based on the selected radio button.
   * Clears the value of the hidden field types to ensure only one link type is submitted.
   */
  toggleLinkType() {
    const isProductLine = this.hasProductLineRadioTarget && this.productLineRadioTarget.checked
    const isCategory = this.hasCategoryRadioTarget && this.categoryRadioTarget.checked
    const isStaticPage = this.hasStaticPageRadioTarget && this.staticPageRadioTarget.checked

    if (isProductLine) {
      this.showProductLineFields()
      this.hideCategoryFields()
      this.hideStaticPageFields()
      this.clearCategorySelection()
      this.clearStaticPageSelection()
    } else if (isCategory) {
      this.hideProductLineFields()
      this.showCategoryFields()
      this.hideStaticPageFields()
      this.clearProductLineSelection()
      this.clearStaticPageSelection()
    } else if (isStaticPage) {
      this.hideProductLineFields()
      this.hideCategoryFields()
      this.showStaticPageFields()
      this.clearProductLineSelection()
      this.clearCategorySelection()
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Private Methods
  // ─────────────────────────────────────────────────────────────────────────────

  showProductLineFields() {
    if (this.hasProductLineFieldsTarget) {
      this.productLineFieldsTarget.classList.remove("d-none")
    }
  }

  hideProductLineFields() {
    if (this.hasProductLineFieldsTarget) {
      this.productLineFieldsTarget.classList.add("d-none")
    }
  }

  showCategoryFields() {
    if (this.hasCategoryFieldsTarget) {
      this.categoryFieldsTarget.classList.remove("d-none")
    }
  }

  hideCategoryFields() {
    if (this.hasCategoryFieldsTarget) {
      this.categoryFieldsTarget.classList.add("d-none")
    }
  }

  showStaticPageFields() {
    if (this.hasStaticPageFieldsTarget) {
      this.staticPageFieldsTarget.classList.remove("d-none")
    }
  }

  hideStaticPageFields() {
    if (this.hasStaticPageFieldsTarget) {
      this.staticPageFieldsTarget.classList.add("d-none")
    }
  }

  clearProductLineSelection() {
    if (this.hasProductLineSelectTarget) {
      this.productLineSelectTarget.value = ""
    }
  }

  clearCategorySelection() {
    if (this.hasCategorySelectTarget) {
      this.categorySelectTarget.value = ""
    }
  }

  clearStaticPageSelection() {
    if (this.hasStaticPageSelectTarget) {
      this.staticPageSelectTarget.value = ""
    }
  }
}
